import React from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import type {
  TurnBlock,
  ControlRequestBlock,
  CodexApprovalRequestBlock,
  CodexCommandResultBlock,
  CodexErrorBlock,
} from './types.js';
import { AskUserOverlayContext } from './AskUserOverlayContext.js';
import { cn } from '@reflex/ui/lib/utils';
import {
  Check,
  X,
  OctagonX,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Terminal,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import { WorkingDirProvider } from '@reflex/ui/components/content/WorkingDirContext';
import { ApiError } from '@reflex/ui/client/http';
import { toErrorMessage } from '@reflex/plugin-api/errors';
import { sendControlResponse } from '../../api.js';

/**
 * Map any error from the control-response POST to a user-facing toast.
 *
 * `409 control_response_no_pending_request` is the stale-prompt path —
 * the agent's underlying turn (the parked promise inside the supervised
 * Claude wrapper) was lost, usually because the connection was rebuilt
 * mid-question. We surface that explicitly so users don't sit staring
 * at a card that silently swallowed their answer.
 */
function toastControlResponseError(err: unknown): void {
  if (err instanceof ApiError && err.status === 409) {
    toast.error('This prompt has expired', {
      description:
        'The agent was reconnected while waiting for your reply, so the original turn is gone. Type a new message in the chat to continue.',
    });
    return;
  }
  toast.error('Failed to send response', {
    description: toErrorMessage(err),
  });
}

import {
  TextBlockView,
  ThinkingBlockView,
  ToolCallBlockView,
  PlanBlockView,
  TaskBlockView,
  ResourceLinkBlockView,
  ImageBlockView,
  AudioBlockView,
  EmbeddedResourceBlockView,
  SystemInitBlockView,
  AgentLogBlockView,
} from './turn-blocks/index.js';

export {
  TextBlockView,
  ThinkingBlockView,
  ToolCallBlockView,
  PlanBlockView,
  TaskBlockView,
  ResourceLinkBlockView,
  ImageBlockView,
  AudioBlockView,
  EmbeddedResourceBlockView,
  SystemInitBlockView,
  AgentLogBlockView,
};

// --- AskUserQuestion ---

import { AskUserQuestionCard } from '../AskUserQuestionCard.js';
import type { AskUserQuestionAnnotation, AskUserQuestionItem } from '../AskUserQuestionCard.js';
import { clearAskUserQuestionDraft } from '../ask-user-question-draft.js';

function isAskUserQuestionBlock(block: ControlRequestBlock): block is ControlRequestBlock & {
  toolInput: { questions: AskUserQuestionItem[] };
} {
  return (
    block.toolName === 'AskUserQuestion' &&
    block.toolInput != null &&
    Array.isArray(block.toolInput.questions) &&
    block.toolInput.questions.length > 0
  );
}

/**
 * Build the control-response payload for an AskUserQuestion interaction.
 * Exported for testing.
 */
export function buildAskUserControlResponse(
  block: Pick<ControlRequestBlock, 'requestId' | 'toolUseId'>,
  questions: AskUserQuestionItem[],
  answers: Record<string, string>,
  annotations?: Record<string, AskUserQuestionAnnotation>,
): Record<string, unknown> {
  const updatedInput: Record<string, unknown> = { questions, answers };
  if (annotations && Object.keys(annotations).length > 0) {
    updatedInput.annotations = annotations;
  }
  const response: Record<string, unknown> = {
    behavior: 'allow',
    updatedInput,
  };
  if (block.toolUseId) response.toolUseID = block.toolUseId;
  return {
    type: 'control_response',
    response: { subtype: 'success', request_id: block.requestId, response },
  };
}

/**
 * Build a `deny` control-response for an AskUserQuestion the user declined.
 * Skip uses this without `interrupt` (the model learns the user passed and
 * continues the turn); Dismiss sets `interrupt` so the turn is aborted.
 * Exported for testing.
 */
export function buildAskUserDenyResponse(
  block: Pick<ControlRequestBlock, 'requestId' | 'toolUseId'>,
  message: string,
  interrupt?: boolean,
): Record<string, unknown> {
  const response: Record<string, unknown> = { behavior: 'deny', message };
  if (block.toolUseId) response.toolUseID = block.toolUseId;
  if (interrupt) response.interrupt = true;
  return {
    type: 'control_response',
    response: { subtype: 'success', request_id: block.requestId, response },
  };
}

function formatToolName(name: string): string {
  return name.replace(/_/g, ' ');
}

function summarizeToolInput(input: unknown): string | null {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  if (typeof obj.command === 'string') return obj.command;
  if (typeof obj.path === 'string') return obj.path;
  if (typeof obj.url === 'string') return obj.url;
  const keys = Object.keys(obj);
  if (keys.length === 0) return null;
  return keys.slice(0, 3).join(', ');
}

/**
 * Extract a `Record<string, string>` of question text → answer label from a
 * merged control_request `toolInput`. The Claude stream echoes the original
 * `buildAskUserControlResponse` payload back as `control_response`, and
 * `handleControlResponse` merges `updatedInput.answers` onto the block — see
 * the SDK chat handler. Non-object / non-string entries are ignored so a
 * malformed echo doesn't render garbage. Exported for testing.
 */
export function extractAskUserAnswers(input: unknown): Record<string, string> | null {
  if (!input || typeof input !== 'object') return null;
  const raw = (input as { answers?: unknown }).answers;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

/**
 * Extract the per-question `notes` the user attached, keyed by question text,
 * from a merged control_request `toolInput`. Mirrors `extractAskUserAnswers`:
 * the stream echoes `updatedInput.annotations` back onto the block. Entries
 * without string notes are skipped. Exported for testing.
 */
export function extractAskUserNotes(input: unknown): Record<string, string> | null {
  if (!input || typeof input !== 'object') return null;
  const raw = (input as { annotations?: unknown }).annotations;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const notes = v && typeof v === 'object' ? (v as { notes?: unknown }).notes : undefined;
    if (typeof notes === 'string' && notes.length > 0) out[k] = notes;
  }
  return out;
}

/**
 * Collapsed "Answered / Skipped" summary for a resolved question prompt,
 * expandable to the per-question answers. Shared by the Claude
 * AskUserQuestion and native Codex requestUserInput views.
 */
function AskUserAnsweredSummary({
  blockId,
  questions,
  answers,
  notes,
  dismissed,
  sourceEvent,
}: {
  blockId: string;
  questions: AskUserQuestionItem[];
  answers: Record<string, string>;
  notes: Record<string, string>;
  dismissed: boolean;
  sourceEvent?: ControlRequestBlock['sourceEvent'];
}) {
  const [expanded, setExpanded] = React.useState(false);
  const answeredCount = questions.filter((q) => {
    const v = answers[q.question];
    return typeof v === 'string' && v.length > 0;
  }).length;
  const totalQuestions = questions.length;
  const summary = dismissed
    ? 'Dismissed'
    : totalQuestions === 1
      ? answeredCount === 1
        ? 'Answered'
        : 'Skipped'
      : `Answered ${answeredCount} of ${totalQuestions}`;

  return (
    <div
      className="group/ask rounded-md border border-border/40 bg-card/50"
      data-testid="ask-user-question-answered"
    >
      <Button
        variant="ghost"
        onClick={() => setExpanded((v) => !v)}
        className="h-auto w-full justify-start gap-1.5 rounded-md px-2 py-1.5 text-left text-xs font-normal hover:bg-accent/40 dark:hover:bg-accent/40"
        aria-expanded={expanded}
        aria-controls={`ask-user-detail-${blockId}`}
      >
        {expanded ? (
          <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3 shrink-0 text-muted-foreground" />
        )}
        <Check className="size-3 shrink-0 text-status-success" />
        <span className="text-status-success">{summary}</span>
        {!expanded && questions[0] && (
          <code className="truncate rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground/60">
            {questions[0].question.length > 60
              ? questions[0].question.slice(0, 60) + '...'
              : questions[0].question}
          </code>
        )}
        {sourceEvent && (
          <span className="ml-auto opacity-0 transition-opacity group-hover/ask:opacity-100">
            <EventDetailTooltip event={sourceEvent} />
          </span>
        )}
      </Button>
      {expanded && (
        <ol
          id={`ask-user-detail-${blockId}`}
          className="flex flex-col gap-2 border-t border-border/40 px-3 py-2 text-xs"
        >
          {questions.map((q, qi) => {
            const answer = answers[q.question];
            const hasAnswer = typeof answer === 'string' && answer.length > 0;
            return (
              <li key={qi} className="flex flex-col gap-0.5">
                <span className="text-foreground/80">
                  {totalQuestions > 1 && (
                    <span className="font-semibold text-foreground/70">{qi + 1}.&nbsp;</span>
                  )}
                  {q.question}
                </span>
                <span
                  className={cn(
                    'pl-3 font-medium',
                    hasAnswer ? 'text-foreground' : 'italic text-muted-foreground',
                  )}
                >
                  {hasAnswer ? answer : 'Skipped'}
                </span>
                {notes[q.question] && (
                  <span className="pl-3 text-[11px] italic text-muted-foreground">
                    Note: {notes[q.question]}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/**
 * Wrapper around a live prompt card. A shrinkable flex column, not a plain
 * block: the docked overlay slot constrains its own height on a short viewport,
 * and a block wrapper would swallow that constraint and let the card render at
 * natural height with its footer clipped below the fold. `min-h-0` keeps the
 * card free to shrink so its middle section scrolls instead.
 */
const ASK_CARD_WRAPPER_CLASS = 'group/ask relative flex min-h-0 flex-col';

export function AskUserQuestionBlockView({
  block,
  agentId,
  isLive,
}: {
  block: ControlRequestBlock;
  agentId?: string;
  isLive?: boolean;
}) {
  const overlay = React.useContext(AskUserOverlayContext);
  const questions = (block.toolInput as { questions: AskUserQuestionItem[] }).questions;
  const [submitted, setSubmitted] = React.useState(false);
  const [isPending, setIsPending] = React.useState(false);
  // Fallback for when the stream echo is lossy: keep the user's submission
  // locally so the same session can still render answers.
  const [localAnswers, setLocalAnswers] = React.useState<Record<string, string> | null>(null);
  const [dismissed, setDismissed] = React.useState(false);

  const isResolved = block.responded || submitted;

  // Keyed per prompt so in-progress answers survive the session-switch
  // remount (`key={agent.id}` above us). Cleared on every resolution path,
  // including `block.responded` arriving via the stream echo so a prompt
  // answered elsewhere doesn't leave a stale draft behind.
  const draftKey = agentId ? `${agentId}:${block.requestId}` : undefined;

  React.useEffect(() => {
    if (block.responded && draftKey) clearAskUserQuestionDraft(draftKey);
  }, [block.responded, draftKey]);

  const handleSubmit = React.useCallback(
    async (
      answers: Record<string, string>,
      annotations: Record<string, AskUserQuestionAnnotation>,
    ) => {
      if (!agentId) return;
      setSubmitted(true);
      setLocalAnswers(answers);
      setIsPending(true);
      try {
        const controlResponse = buildAskUserControlResponse(block, questions, answers, annotations);
        await sendControlResponse(agentId, controlResponse);
        if (draftKey) clearAskUserQuestionDraft(draftKey);
      } catch (err) {
        setSubmitted(false);
        setLocalAnswers(null);
        toastControlResponseError(err);
      } finally {
        setIsPending(false);
      }
    },
    [agentId, block, draftKey, questions],
  );

  const handleSkip = React.useCallback(async () => {
    if (!agentId) return;
    setSubmitted(true);
    setLocalAnswers({});
    setIsPending(true);
    try {
      const controlResponse = buildAskUserDenyResponse(
        block,
        'The user skipped the question(s) without answering. Continue using your best judgment.',
      );
      await sendControlResponse(agentId, controlResponse);
      if (draftKey) clearAskUserQuestionDraft(draftKey);
    } catch (err) {
      setSubmitted(false);
      setLocalAnswers(null);
      toastControlResponseError(err);
    } finally {
      setIsPending(false);
    }
  }, [agentId, block, draftKey]);

  const handleDismiss = React.useCallback(async () => {
    if (!agentId) return;
    setSubmitted(true);
    setDismissed(true);
    setLocalAnswers({});
    setIsPending(true);
    try {
      const controlResponse = buildAskUserDenyResponse(
        block,
        'The user dismissed the question(s) and interrupted the turn.',
        true,
      );
      await sendControlResponse(agentId, controlResponse);
      if (draftKey) clearAskUserQuestionDraft(draftKey);
    } catch (err) {
      setSubmitted(false);
      setDismissed(false);
      setLocalAnswers(null);
      toastControlResponseError(err);
    } finally {
      setIsPending(false);
    }
  }, [agentId, block, draftKey]);

  // While the question is unanswered, register it with the host so the
  // transcript can be dimmed and the composer disabled. The card itself is
  // lifted into the shared overlay below.
  React.useEffect(() => {
    if (!overlay.enabled) return;
    overlay.setPending(block.id, !isResolved);
    return () => overlay.setPending(block.id, false);
  }, [overlay.enabled, overlay.setPending, block.id, isResolved]);

  if (isResolved) {
    return (
      <AskUserAnsweredSummary
        blockId={block.id}
        questions={questions}
        answers={extractAskUserAnswers(block.toolInput) ?? localAnswers ?? {}}
        notes={extractAskUserNotes(block.toolInput) ?? {}}
        dismissed={dismissed}
        sourceEvent={block.sourceEvent}
      />
    );
  }

  const card = (
    <div className={ASK_CARD_WRAPPER_CLASS}>
      <AskUserQuestionCard
        questions={questions}
        onSubmit={(answers, annotations) => void handleSubmit(answers, annotations)}
        onSkip={() => void handleSkip()}
        onDismiss={() => void handleDismiss()}
        isPending={isPending}
        draftKey={draftKey}
        initiallyCollapsed={!overlay.enabled && isLive === false}
      />
      {block.sourceEvent && (
        <span className="pointer-events-none absolute right-2 top-2 opacity-0 transition-opacity group-hover/ask:pointer-events-auto group-hover/ask:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
    </div>
  );

  // Lift the prompt into the shared overlay so it floats above the transcript
  // as a prominent popover. Render nothing inline until the overlay slot is
  // mounted (avoids a one-frame inline flash). Falls back to inline when no
  // overlay provider is present (e.g. isolated renderer stories).
  if (overlay.enabled) {
    return overlay.overlayEl ? createPortal(card, overlay.overlayEl) : null;
  }
  return card;
}

export function ControlRequestBlockView({
  block,
  agentId,
  onRespond,
}: {
  block: ControlRequestBlock;
  agentId?: string;
  onRespond?: (requestId: string, behavior: 'allow' | 'deny', interrupt?: boolean) => void;
}) {
  const [decision, setDecision] = React.useState<'allow' | 'deny' | null>(null);
  const [alwaysAllow, setAlwaysAllow] = React.useState(false);
  const [isPending, setIsPending] = React.useState(false);

  const responded = block.responded || decision !== null;
  const inputSummary = summarizeToolInput(block.toolInput);
  const title =
    block.requestSubtype === 'can_use_tool' && block.toolName
      ? `Permission: ${formatToolName(block.toolName)}`
      : 'Permission request';

  const handleAllow = React.useCallback(async () => {
    if (!agentId || responded) return;
    setDecision('allow');
    setIsPending(true);
    try {
      const toolInput = block.toolInput ?? {};
      const response: Record<string, unknown> = {
        behavior: 'allow',
        updatedInput: toolInput,
      };
      if (block.toolUseId) response.toolUseID = block.toolUseId;
      if (alwaysAllow && block.toolName) {
        response.updatedPermissions = [
          {
            type: 'addRules',
            rules: [{ toolName: block.toolName }],
            behavior: 'allow',
            destination: 'session',
          },
        ];
      }
      const controlResponse = {
        type: 'control_response',
        response: { subtype: 'success', request_id: block.requestId, response },
      };
      await sendControlResponse(agentId, controlResponse);
      onRespond?.(block.requestId, 'allow');
    } catch (err) {
      setDecision(null);
      toastControlResponseError(err);
    } finally {
      setIsPending(false);
    }
  }, [agentId, responded, block, alwaysAllow, onRespond]);

  const handleDeny = React.useCallback(
    async (interrupt = false) => {
      if (!agentId || responded) return;
      setDecision('deny');
      setIsPending(true);
      try {
        const response: Record<string, unknown> = {
          behavior: 'deny',
          message: 'User denied the request',
        };
        if (block.toolUseId) response.toolUseID = block.toolUseId;
        if (interrupt) response.interrupt = true;
        const controlResponse = {
          type: 'control_response',
          response: { subtype: 'success', request_id: block.requestId, response },
        };
        await sendControlResponse(agentId, controlResponse);
        onRespond?.(block.requestId, 'deny', interrupt);
      } catch (err) {
        setDecision(null);
        toastControlResponseError(err);
      } finally {
        setIsPending(false);
      }
    },
    [agentId, responded, block, onRespond],
  );

  if (responded) {
    const isAllow = decision === 'allow' || (block.responded && decision === null);
    return (
      <div className="group/control flex items-center gap-1.5 rounded-md px-2 py-1 text-xs">
        {isAllow ? (
          <Check className="h-3 w-3 shrink-0 text-status-success" />
        ) : (
          <X className="h-3 w-3 shrink-0 text-status-error" />
        )}
        <span className={cn('text-xs', isAllow ? 'text-status-success' : 'text-status-error')}>
          {isAllow ? 'Allowed' : 'Denied'}
        </span>
        {block.toolName && (
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground/60">
            {block.toolName}
          </code>
        )}
        {block.sourceEvent && (
          <span className="ml-auto opacity-0 transition-opacity group-hover/control:opacity-100">
            <EventDetailTooltip event={block.sourceEvent} />
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="group/control mx-auto my-3 w-full max-w-2xl overflow-hidden rounded-xl border border-status-warning/40 bg-card shadow-sm">
      <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2">
        <ShieldAlert className="h-4 w-4 shrink-0 text-status-warning" />
        <span className="flex-1 text-xs font-medium text-status-warning">{title}</span>
        {block.sourceEvent && (
          <span className="opacity-0 transition-opacity group-hover/control:opacity-100">
            <EventDetailTooltip event={block.sourceEvent} />
          </span>
        )}
      </div>

      {block.toolName && (
        <div className="px-3 py-2">
          <p className="text-xs text-muted-foreground">
            The agent wants to use{' '}
            <span className="font-medium text-foreground">{block.toolName}</span>
            {inputSummary && (
              <>
                {' '}
                with <code className="rounded bg-muted px-1 py-0.5 text-xs">{inputSummary}</code>
              </>
            )}
          </p>
        </div>
      )}

      <div className="space-y-2 px-3 pb-3">
        {block.toolName && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={alwaysAllow}
              onChange={(e) => setAlwaysAllow(e.target.checked)}
              className="h-3 w-3 rounded border-border/60"
            />
            Always allow <span className="font-medium">{block.toolName}</span> this session
          </label>
        )}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 border-status-success/40 text-status-success hover:bg-status-success/10 hover:text-status-success"
            onClick={handleAllow}
            disabled={isPending || !agentId}
          >
            <Check className="h-3.5 w-3.5" />
            Allow
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 border-status-error/40 text-status-error hover:bg-status-error/10 hover:text-status-error"
            onClick={() => handleDeny(false)}
            disabled={isPending || !agentId}
          >
            <X className="h-3.5 w-3.5" />
            Deny
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 border-status-error/40 text-status-error hover:bg-status-error/10 hover:text-status-error"
                onClick={() => handleDeny(true)}
                disabled={isPending || !agentId}
              >
                <OctagonX className="h-3.5 w-3.5" />
                Interrupt
              </Button>
            </TooltipTrigger>
            <TooltipContent>Deny and interrupt the agent</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

// --- Native Codex requestUserInput ---

/** One native Codex `item/tool/requestUserInput` question, as on the wire. */
export interface CodexUserInputQuestion {
  id: string;
  header?: string;
  question: string;
  isOther?: boolean;
  isSecret?: boolean;
  options?: Array<{ label: string; description?: string }> | null;
}

/**
 * Extract the questions from a native Codex `item/tool/requestUserInput`
 * approval block. Returns null for other approval methods or malformed
 * payloads, which fall back to the display-only approval banner. Exported
 * for testing.
 */
export function extractCodexUserInputQuestions(
  block: CodexApprovalRequestBlock,
): CodexUserInputQuestion[] | null {
  if (block.method !== 'item/tool/requestUserInput') return null;
  const params = block.extra?.params as { questions?: unknown } | undefined;
  if (!Array.isArray(params?.questions)) return null;
  const questions = params.questions.filter(
    (q): q is CodexUserInputQuestion =>
      q != null &&
      typeof q === 'object' &&
      typeof (q as { id?: unknown }).id === 'string' &&
      typeof (q as { question?: unknown }).question === 'string',
  );
  return questions.length > 0 ? questions : null;
}

/**
 * Build the JSON-RPC reply for a native Codex `requestUserInput` question
 * set: `{ id, result: { answers: { <questionId>: { answers: [...] } } } }`.
 * The selected option label is the answer; free-form notes ride along as an
 * extra answer string (Codex accepts arbitrary text, and `isOther` questions
 * may have no matching option at all). Exported for testing.
 */
export function buildCodexUserInputResponse(
  block: Pick<CodexApprovalRequestBlock, 'requestId' | 'extra'>,
  questions: CodexUserInputQuestion[],
  answers: Record<string, string>,
  annotations?: Record<string, AskUserQuestionAnnotation>,
): Record<string, unknown> {
  const byId: Record<string, { answers: string[] }> = {};
  for (const question of questions) {
    const selected = answers[question.question];
    const notes = annotations?.[question.question]?.notes;
    const values: string[] = [];
    if (typeof selected === 'string' && selected.length > 0) values.push(selected);
    if (typeof notes === 'string' && notes.length > 0) values.push(notes);
    if (values.length > 0) byId[question.id] = { answers: values };
  }
  const rpcId = block.extra?.rpcId;
  const id = typeof rpcId === 'number' || typeof rpcId === 'string' ? rpcId : block.requestId;
  return { id, result: { answers: byId } };
}

/** A confirmation-only Codex tool suggestion carried over MCP elicitation. */
export interface CodexToolSuggestion {
  message: string;
  toolName: string;
  actionLabel: string;
}

/**
 * Extract the confirmation-only tool suggestion shape Codex emits for plugin
 * recommendations. Structured elicitation forms with actual fields remain on
 * the safe fallback path until the chat has controls for their schemas.
 */
export function extractCodexToolSuggestion(
  block: CodexApprovalRequestBlock,
): CodexToolSuggestion | null {
  if (block.method !== 'mcpServer/elicitation/request') return null;
  const params = block.extra?.params;
  if (params == null || typeof params !== 'object' || Array.isArray(params)) return null;
  const { mode, message, requestedSchema } = params as Record<string, unknown>;
  if (
    (mode !== 'form' && mode !== 'openai/form') ||
    typeof message !== 'string' ||
    message.length === 0
  ) {
    return null;
  }
  if (
    requestedSchema == null ||
    typeof requestedSchema !== 'object' ||
    Array.isArray(requestedSchema)
  ) {
    return null;
  }
  const properties = (requestedSchema as Record<string, unknown>).properties;
  if (
    properties == null ||
    typeof properties !== 'object' ||
    Array.isArray(properties) ||
    Object.keys(properties).length > 0
  ) {
    return null;
  }
  const meta = (params as Record<string, unknown>)._meta;
  if (meta == null || typeof meta !== 'object' || Array.isArray(meta)) return null;
  const approvalMeta = meta as Record<string, unknown>;
  if (
    approvalMeta.codex_approval_kind !== 'tool_suggestion' ||
    typeof approvalMeta.tool_name !== 'string' ||
    approvalMeta.tool_name.length === 0
  ) {
    return null;
  }
  const toolName = approvalMeta.tool_name;
  const actionLabel =
    approvalMeta.suggest_type === 'install' ? `Install ${toolName}` : `Use ${toolName}`;
  return { message, toolName, actionLabel };
}

/** Build the JSON-RPC reply for a confirmation-only MCP elicitation. */
export function buildCodexElicitationResponse(
  block: Pick<CodexApprovalRequestBlock, 'requestId' | 'extra'>,
  action: 'accept' | 'decline' | 'cancel',
): Record<string, unknown> {
  const rpcId = block.extra?.rpcId;
  const id = typeof rpcId === 'number' || typeof rpcId === 'string' ? rpcId : block.requestId;
  return {
    id,
    result: {
      action,
      content: action === 'accept' ? {} : null,
      _meta: null,
    },
  };
}

/**
 * Interactive card for a native Codex `item/tool/requestUserInput` request.
 * Reuses the AskUserQuestion card UI; the answer resolves the provider's
 * parked approval handler via the control-response route, and the durable
 * JSON-RPC `response` echo marks the block answered across sessions.
 */
export function CodexUserInputBlockView({
  block,
  questions,
  agentId,
  isLive,
}: {
  block: CodexApprovalRequestBlock;
  questions: CodexUserInputQuestion[];
  agentId?: string;
  isLive?: boolean;
}) {
  const overlay = React.useContext(AskUserOverlayContext);
  const [submitted, setSubmitted] = React.useState(false);
  const [isPending, setIsPending] = React.useState(false);
  const [localAnswers, setLocalAnswers] = React.useState<Record<string, string> | null>(null);
  const [dismissed, setDismissed] = React.useState(false);

  const cardQuestions: AskUserQuestionItem[] = React.useMemo(
    () =>
      questions.map((question) => ({
        header: question.header || undefined,
        question: question.question,
        options: (question.options ?? []).map((option) => ({
          label: option.label,
          description: option.description || undefined,
        })),
      })),
    [questions],
  );

  const isResolved = Boolean(block.responded) || submitted;
  const draftKey = agentId ? `${agentId}:${block.requestId}` : undefined;

  React.useEffect(() => {
    if (block.responded && draftKey) clearAskUserQuestionDraft(draftKey);
  }, [block.responded, draftKey]);

  const respond = React.useCallback(
    async (
      payload: Record<string, unknown>,
      answers: Record<string, string>,
      markDismissed?: boolean,
    ) => {
      if (!agentId) return;
      setSubmitted(true);
      setLocalAnswers(answers);
      if (markDismissed) setDismissed(true);
      setIsPending(true);
      try {
        await sendControlResponse(agentId, payload);
        if (draftKey) clearAskUserQuestionDraft(draftKey);
      } catch (err) {
        setSubmitted(false);
        setLocalAnswers(null);
        setDismissed(false);
        toastControlResponseError(err);
      } finally {
        setIsPending(false);
      }
    },
    [agentId, draftKey],
  );

  const handleSubmit = React.useCallback(
    (answers: Record<string, string>, annotations: Record<string, AskUserQuestionAnnotation>) =>
      respond(buildCodexUserInputResponse(block, questions, answers, annotations), answers),
    [block, questions, respond],
  );

  // Codex has no deny/interrupt semantics on this request: both Skip and
  // Dismiss resolve with empty answers and the model continues on its own
  // judgment. Dismiss only changes the collapsed summary label.
  const handleSkip = React.useCallback(
    (markDismissed?: boolean) =>
      respond(buildCodexUserInputResponse(block, questions, {}), {}, markDismissed),
    [block, questions, respond],
  );

  React.useEffect(() => {
    if (!overlay.enabled) return;
    overlay.setPending(block.id, !isResolved);
    return () => overlay.setPending(block.id, false);
  }, [overlay.enabled, overlay.setPending, block.id, isResolved]);

  if (isResolved) {
    const wireAnswers = (
      block.result as { answers?: Record<string, { answers?: string[] }> } | undefined
    )?.answers;
    const answers: Record<string, string> = {};
    for (const question of questions) {
      const values = wireAnswers?.[question.id]?.answers;
      if (Array.isArray(values) && values.length > 0) {
        answers[question.question] = values.filter((v) => typeof v === 'string').join(', ');
      }
    }
    return (
      <AskUserAnsweredSummary
        blockId={block.id}
        questions={cardQuestions}
        answers={Object.keys(answers).length > 0 ? answers : (localAnswers ?? {})}
        notes={{}}
        dismissed={dismissed}
        sourceEvent={block.sourceEvent}
      />
    );
  }

  const card = (
    <div className={ASK_CARD_WRAPPER_CLASS}>
      <AskUserQuestionCard
        questions={cardQuestions}
        onSubmit={(answers, annotations) => void handleSubmit(answers, annotations)}
        onSkip={() => void handleSkip()}
        onDismiss={() => void handleSkip(true)}
        isPending={isPending}
        draftKey={draftKey}
        initiallyCollapsed={!overlay.enabled && isLive === false}
      />
      {block.sourceEvent && (
        <span className="pointer-events-none absolute right-2 top-2 opacity-0 transition-opacity group-hover/ask:pointer-events-auto group-hover/ask:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
    </div>
  );

  if (overlay.enabled) {
    return overlay.overlayEl ? createPortal(card, overlay.overlayEl) : null;
  }
  return card;
}

/**
 * Interactive card for a confirmation-only Codex MCP tool suggestion.
 * Continue accepts the suggestion, Skip declines it, and Dismiss cancels it.
 */
export function CodexToolSuggestionBlockView({
  block,
  suggestion,
  agentId,
  isLive,
}: {
  block: CodexApprovalRequestBlock;
  suggestion: CodexToolSuggestion;
  agentId?: string;
  isLive?: boolean;
}) {
  const overlay = React.useContext(AskUserOverlayContext);
  const [localAction, setLocalAction] = React.useState<'accept' | 'decline' | 'cancel' | null>(
    null,
  );
  const [isPending, setIsPending] = React.useState(false);
  const cardQuestions: AskUserQuestionItem[] = React.useMemo(
    () => [
      {
        header: 'Tool suggestion',
        question: suggestion.message,
        options: [{ label: suggestion.actionLabel }],
      },
    ],
    [suggestion.actionLabel, suggestion.message],
  );
  const isResolved = Boolean(block.responded) || localAction != null;
  const draftKey = agentId ? `${agentId}:${block.requestId}` : undefined;

  React.useEffect(() => {
    if (block.responded && draftKey) clearAskUserQuestionDraft(draftKey);
  }, [block.responded, draftKey]);

  React.useEffect(() => {
    if (!overlay.enabled) return;
    overlay.setPending(block.id, !isResolved);
    return () => overlay.setPending(block.id, false);
  }, [overlay.enabled, overlay.setPending, block.id, isResolved]);

  const respond = React.useCallback(
    async (action: 'accept' | 'decline' | 'cancel') => {
      if (!agentId) return;
      setLocalAction(action);
      setIsPending(true);
      try {
        await sendControlResponse(agentId, buildCodexElicitationResponse(block, action));
        if (draftKey) clearAskUserQuestionDraft(draftKey);
      } catch (err) {
        setLocalAction(null);
        toastControlResponseError(err);
      } finally {
        setIsPending(false);
      }
    },
    [agentId, block, draftKey],
  );

  if (isResolved) {
    const wireAction = (block.result as { action?: unknown } | undefined)?.action;
    const action =
      wireAction === 'accept' || wireAction === 'decline' || wireAction === 'cancel'
        ? wireAction
        : localAction;
    return (
      <AskUserAnsweredSummary
        blockId={block.id}
        questions={cardQuestions}
        answers={action === 'accept' ? { [suggestion.message]: suggestion.actionLabel } : {}}
        notes={{}}
        dismissed={action === 'cancel'}
        sourceEvent={block.sourceEvent}
      />
    );
  }

  const card = (
    <div className={ASK_CARD_WRAPPER_CLASS}>
      <AskUserQuestionCard
        questions={cardQuestions}
        onSubmit={() => void respond('accept')}
        onSkip={() => void respond('decline')}
        onDismiss={() => void respond('cancel')}
        isPending={isPending}
        draftKey={draftKey}
        initiallyCollapsed={!overlay.enabled && isLive === false}
      />
      {block.sourceEvent && (
        <span className="pointer-events-none absolute right-2 top-2 opacity-0 transition-opacity group-hover/ask:pointer-events-auto group-hover/ask:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
    </div>
  );

  if (overlay.enabled) {
    return overlay.overlayEl ? createPortal(card, overlay.overlayEl) : null;
  }
  return card;
}

/**
 * Native Codex approvals other than the interactive input shapes above are
 * auto-resolved by the provider. Keep a visible transcript affordance without
 * offering controls that would send the legacy Claude control-response shape.
 */
export function CodexApprovalRequestBlockView({ block }: { block: CodexApprovalRequestBlock }) {
  return (
    <div
      className="group/codex-approval mx-auto my-3 w-full max-w-2xl overflow-hidden rounded-xl border border-status-warning/40 bg-card shadow-sm"
      data-testid="codex-approval-request"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <ShieldAlert className="icon-md shrink-0 text-status-warning" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-status-warning">{block.title}</p>
          {block.detail && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{block.detail}</p>
          )}
        </div>
        <span className="rounded-full bg-status-warning/10 px-2 py-0.5 text-xs text-status-warning">
          Needs input
        </span>
        {block.sourceEvent && (
          <span className="opacity-0 transition-opacity group-hover/codex-approval:opacity-100">
            <EventDetailTooltip event={block.sourceEvent} />
          </span>
        )}
      </div>
    </div>
  );
}

export function CodexErrorBlockView({ block }: { block: CodexErrorBlock }) {
  return (
    <div
      className="group/codex-error mx-auto my-3 w-full max-w-2xl rounded-xl border border-status-error/40 bg-status-error/5 px-3 py-2"
      data-testid="codex-error"
    >
      <div className="flex items-start gap-2">
        <CircleAlert className="icon-md mt-0.5 shrink-0 text-status-error" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-status-error">Codex error</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{block.message}</p>
          {block.willRetry && (
            <p className="mt-1 text-xs text-muted-foreground">Retrying automatically</p>
          )}
        </div>
        {block.sourceEvent && (
          <span className="opacity-0 transition-opacity group-hover/codex-error:opacity-100">
            <EventDetailTooltip event={block.sourceEvent} />
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * System-note card for a native Codex slash-command result. Echoes the
 * command in monospace with the provider's outcome message below (multiline
 * for `/help`); error results reuse the codex-error styling treatment.
 */
export function CodexCommandResultBlockView({ block }: { block: CodexCommandResultBlock }) {
  const isError = block.status === 'error';
  return (
    <div
      className={cn(
        'group/codex-command mx-auto my-3 w-full max-w-2xl rounded-xl border px-3 py-2',
        isError ? 'border-status-error/40 bg-status-error/5' : 'border-border bg-muted/30',
      )}
      data-testid="codex-command-result"
    >
      <div className="flex items-start gap-2">
        <Terminal
          className={cn(
            'icon-md mt-0.5 shrink-0',
            isError ? 'text-status-error' : 'text-muted-foreground',
          )}
        />
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'font-mono text-xs font-medium',
              isError ? 'text-status-error' : 'text-foreground',
            )}
          >
            {block.command}
          </p>
          <p className="mt-0.5 text-xs whitespace-pre-wrap text-muted-foreground">
            {block.message}
          </p>
        </div>
        {block.sourceEvent && (
          <span className="opacity-0 transition-opacity group-hover/codex-command:opacity-100">
            <EventDetailTooltip event={block.sourceEvent} />
          </span>
        )}
      </div>
    </div>
  );
}

function renderBlock(
  block: TurnBlock,
  agentId?: string,
  isLive?: boolean,
  isLastBlock?: boolean,
): React.ReactNode {
  switch (block.type) {
    case 'thinking':
      return <ThinkingBlockView block={block} />;
    case 'text':
      return <TextBlockView block={block} showCursor={isLive && isLastBlock} />;
    case 'tool_call':
      return <ToolCallBlockView block={block} agentId={agentId} />;
    case 'plan':
      return <PlanBlockView block={block} />;
    case 'task':
      return <TaskBlockView block={block} />;
    case 'system_init':
      return <SystemInitBlockView block={block} />;
    case 'image':
      return <ImageBlockView block={block} />;
    case 'resource_link':
      return <ResourceLinkBlockView block={block} />;
    case 'audio':
      return <AudioBlockView block={block} />;
    case 'resource':
      return <EmbeddedResourceBlockView block={block} />;
    case 'control_request':
      if (isAskUserQuestionBlock(block)) {
        return <AskUserQuestionBlockView block={block} agentId={agentId} isLive={isLive} />;
      }
      return <ControlRequestBlockView block={block} agentId={agentId} />;
    case 'codex_approval': {
      const userInputQuestions = extractCodexUserInputQuestions(block);
      if (userInputQuestions) {
        return (
          <CodexUserInputBlockView
            block={block}
            questions={userInputQuestions}
            agentId={agentId}
            isLive={isLive}
          />
        );
      }
      const toolSuggestion = extractCodexToolSuggestion(block);
      if (toolSuggestion) {
        return (
          <CodexToolSuggestionBlockView
            block={block}
            suggestion={toolSuggestion}
            agentId={agentId}
            isLive={isLive}
          />
        );
      }
      return <CodexApprovalRequestBlockView block={block} />;
    }
    case 'codex_error':
      return <CodexErrorBlockView block={block} />;
    case 'codex_command_result':
      return <CodexCommandResultBlockView block={block} />;
    case 'agent_log':
      return <AgentLogBlockView block={block} />;
    default:
      return null;
  }
}

export function TurnBlockRenderer({
  block,
  agentId,
  isLive,
  isLastBlock,
}: {
  block: TurnBlock;
  agentId?: string;
  isLive?: boolean;
  isLastBlock?: boolean;
}) {
  const rendered = renderBlock(block, agentId, isLive, isLastBlock);
  if (!rendered) return null;
  const sourceEventId =
    'sourceEvent' in block && block.sourceEvent ? block.sourceEvent.id : undefined;
  const body = sourceEventId ? (
    <div data-event-id={sourceEventId}>{rendered}</div>
  ) : (
    <>{rendered}</>
  );
  // A stamped block answers "relative to where?" for itself, so its paths keep
  // the affordance they were rendered with no matter where the session `cd`s
  // afterwards. `undefined` means unstamped — a story, a test, a block built
  // before this existed — and those still read the session-wide directory.
  if (block.workingDir === undefined) return body;
  return <WorkingDirProvider workingDir={block.workingDir}>{body}</WorkingDirProvider>;
}
